import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "ttag";

import type { Plan, Subscription } from "@/features/billing/models";
import { DURATION_IN_MONTHS } from "@/features/billing/models";
import type { Product } from "@/features/products/models";
import { Chip, ScrollArea } from "@/shared/ui";
import { CurrentRequestAlert, PortalSubscriptionPlans } from "./components";

interface ChangeSubscriptionContentProps {
  products: Product[];
}

const transformProductsToPlans = (products: Product[]): Plan[] => {
  return products.flatMap((product) =>
    product.prices.map((price) => ({
      name: product.name,
      subscriptionPriceId: price.subscriptionPriceId,
      price: price.price,
      features: product.features,
      isCompound: product.tags.some((tag) => tag.label === "Compounded"),
      description: product.description,
      disclaimer: product.disclaimer,
      thumbnail: product.thumbnail ?? "#",
      durationInMonths: price.durationInMonths,
      priceId: price.id,
    })),
  );
};

const getSubscriptionDurationOptions = (products: Product[]) => {
  const durations = new Set<Subscription["price"]["durationInMonths"]>();

  const SUBSCRIPTION_DURATION = {
    [DURATION_IN_MONTHS.MONTHLY]: {
      label: t`Monthly`,
      value: DURATION_IN_MONTHS.MONTHLY,
    },
    [DURATION_IN_MONTHS.QUARTERLY]: {
      label: t`Quarterly`,
      value: DURATION_IN_MONTHS.QUARTERLY,
    },
    [DURATION_IN_MONTHS.SEMI_ANNUALLY]: {
      label: t`Semi-annually`,
      value: DURATION_IN_MONTHS.SEMI_ANNUALLY,
    },
    [DURATION_IN_MONTHS.ANNUALLY]: {
      label: t`Annually`,
      value: DURATION_IN_MONTHS.ANNUALLY,
    },
  } as const;

  for (const product of products) {
    for (const price of product.prices) {
      durations.add(price.durationInMonths);
    }
  }

  return Array.from(durations)
    .sort((a, b) => a - b)
    .map((duration) => SUBSCRIPTION_DURATION[duration]);
};

export const ChangeSubscriptionContent = ({
  products,
}: ChangeSubscriptionContentProps) => {
  const { reset } = useFormContext();

  const transformedPlans = useMemo(() => {
    if (products) {
      return transformProductsToPlans(products);
    }
    return [];
  }, [products]);

  const subscriptionDurationOptions = useMemo(() => {
    if (products) {
      return getSubscriptionDurationOptions(products);
    }
    return [];
  }, [products]);

  const [durationInMonths, setDurationInMonths] = useState<
    Subscription["price"]["durationInMonths"] | undefined
  >(subscriptionDurationOptions[0]?.value);

  return (
    <div className="flex grow flex-col gap-6">
      <ScrollArea orientation="horizontal">
        <div className="flex justify-center-safe gap-2.5 [&_*]:shrink-0">
          {subscriptionDurationOptions?.map((option) => (
            <Chip
              key={option.value}
              size="large"
              color={
                durationInMonths === option.value
                  ? "dark-secondary"
                  : "light-secondary"
              }
              onClick={() => {
                setDurationInMonths(option.value);
                reset();
              }}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </ScrollArea>

      <CurrentRequestAlert />

      <div className="grow pt-0 lg:pb-6">
        <PortalSubscriptionPlans
          plans={transformedPlans.filter(
            (plan) => plan.durationInMonths === durationInMonths,
          )}
        />
      </div>
    </div>
  );
};
